/**
 * DelegatedShipmentsPage — center owner sees all the shipments they
 * created on behalf of customers. Newest first.
 *
 *   Server: GET /api/cargo/centers/me/delegated-shipments
 */
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, Package, Plus, Loader, Hash, Phone, MapPin, ArrowLeftRight,
  ChevronLeft, User,
} from 'lucide-react';
import { useDelegatedShipments } from '@/hooks/useCenterDelegated';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '@/components/common/Button';
import type { DelegatedShipmentRow } from '@/api/centerDelegated';

export default function DelegatedShipmentsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data, isLoading } = useDelegatedShipments();

  return (
    <AppLayout>
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-text-secondary mb-4 hover:text-primary"
      >
        <ArrowRight size={18} /> {t('back', 'رجوع')}
      </button>

      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-bold text-primary mb-1 flex items-center gap-2">
              <Package size={20} /> {t('delegated_shipments_title', 'شحنات أنشأتها لعملاء')}
            </h1>
            <p className="text-text-secondary text-sm">
              {t('delegated_shipments_subtitle', 'الشحنات التي أنشأتها لعملاء أتوا للمركز ولا يستخدمون التطبيق.')}
            </p>
          </div>
          <Link to="/center/create-for-customer">
            <Button variant="primary">
              <Plus size={18} className="ml-1" />
              {t('new_for_customer', 'إنشاء جديدة')}
            </Button>
          </Link>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12 text-text-tertiary">
            <Loader size={28} className="animate-spin" />
          </div>
        ) : !data?.rows || data.rows.length === 0 ? (
          <div className="bg-surface border border-border rounded-2xl p-10 text-center">
            <Package size={48} className="mx-auto text-text-tertiary mb-3" />
            <p className="text-text-secondary mb-3">
              {t('delegated_empty', 'لم تنشئ أيّ شحنة لعميل بعد.')}
            </p>
            <Link to="/center/create-for-customer">
              <Button variant="primary">
                <Plus size={16} className="ml-1" />
                {t('create_first_for_customer', 'أنشئ أول شحنة لعميل')}
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {data.rows.map((row) => <Row key={row.id} row={row} />)}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function Row({ row }: { row: DelegatedShipmentRow }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/shipments/${row.id}`}
      className="block bg-surface border border-border rounded-2xl p-4 hover:border-primary/40 transition-all"
    >
      <div className="flex items-start justify-between gap-3 mb-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <Hash size={14} className="text-text-tertiary" />
            <span className="font-bold text-text-primary" dir="ltr">{row.tracking_number}</span>
          </div>
          {row.sender_name && (
            <div className="flex items-center gap-1 text-sm text-text-secondary">
              <User size={11} /> {row.sender_name}
            </div>
          )}
          {row.sender_phone && (
            <div className="flex items-center gap-1 text-xs text-text-tertiary">
              <Phone size={11} /> <span dir="ltr">{row.sender_phone}</span>
            </div>
          )}
        </div>
        <span className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary font-bold">
          {row.status}
        </span>
        <ChevronLeft size={18} className="text-text-tertiary" />
      </div>

      {(row.pickup_city || row.receiver_city) && (
        <div className="flex items-center gap-2 text-xs text-text-tertiary border-t border-border/50 pt-2 mt-2">
          <MapPin size={11} />
          <span>{row.pickup_city || '-'}</span>
          <ArrowLeftRight size={11} />
          <span>{row.receiver_city || '-'}</span>
        </div>
      )}
      <div className="flex justify-between items-center text-xs text-text-tertiary mt-2 pt-2 border-t border-border/50">
        <span>
          {t('created_at', 'أُنشئت')}: {new Date(row.created_at).toLocaleString('ar-SY')}
        </span>
        {row.price != null && (
          <span className="font-bold text-text-primary">
            {Number(row.price).toLocaleString()} {row.currency}
          </span>
        )}
      </div>
    </Link>
  );
}
